import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { clearSessionStateKeys } from '../hooks/useSessionState';
import {
  AuthUser as User,
  buildAuthUserFromSupabaseUser,
  getSupabaseUserMetadataRole,
  resolveProfileRole,
} from './authUserResolver';

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, first_name?: string, last_name?: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
  error: string | null;
  impersonationData: { clientId: string; clientName: string; userEmail: string; userId: string } | null;
  storeAdminSession: () => Promise<void>;
  restoreAdminSession: () => Promise<boolean>;
  switchToClientSession: (accessToken: string, refreshToken: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [impersonationData, setImpersonationData] = useState<{
    clientId: string;
    clientName: string;
    userEmail: string;
    userId: string;
  } | null>(null);

  // Check for impersonation data (for UI display only, does NOT affect auth)
  useEffect(() => {
    const checkImpersonation = () => {
      const data = localStorage.getItem('admin_impersonation');
      if (data) {
        try {
          const parsed = JSON.parse(data);
          const newData = {
            clientId: parsed.clientId,
            clientName: parsed.clientName,
            userEmail: parsed.userEmail,
            userId: parsed.userId,
          };
          // Only update if data actually changed
          setImpersonationData(prev => {
            if (prev && JSON.stringify(prev) === JSON.stringify(newData)) {
              return prev; // No change
            }
            return newData;
          });
        } catch (e) {
          console.error('Invalid impersonation data:', e);
          localStorage.removeItem('admin_impersonation');
          setImpersonationData(null);
        }
      } else {
        setImpersonationData(null);
      }
    };

    checkImpersonation();
    // Only listen for storage events (cross-tab), no polling
    window.addEventListener('storage', checkImpersonation);
    return () => {
      window.removeEventListener('storage', checkImpersonation);
    };
  }, []);

  // Check if user is authenticated on mount only (NOT when impersonation changes)
  useEffect(() => {
    let isMounted = true;
    
    const checkAuth = async () => {
      try {
        // Get current session
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!isMounted) return;
        
        if (session?.user) {
          
          const role = await resolveProfileRole({
            supabase,
            userId: session.user.id,
            fallbackRole: getSupabaseUserMetadataRole(session.user),
            shouldUseResult: () => isMounted,
            onProfileError: (profileError) => {
              console.warn('Profile query error (using metadata role):', {
                message: profileError.message,
                code: profileError.code,
                details: profileError.details,
                hint: profileError.hint
              });
            },
            onFetchError: (profileError) => {
              console.warn('Could not fetch profile role, using metadata:', {
                message: profileError?.message || 'Unknown error',
                name: profileError?.name
              });
            },
            onRoleFound: (profileRole) => {
              console.log('Role from profiles table:', profileRole);
            },
          });
          
          if (!isMounted) return;
          
          // CRITICAL: user object ALWAYS reflects actual authenticated user (admin)
          // NEVER modify based on impersonation - that causes auth deadlocks
          setUser(buildAuthUserFromSupabaseUser(session.user, role));
          
          console.log('👤 User object set (actual authenticated user):', role);
        } else {
          console.log('❌ No active session');
          if (!isMounted) return;
          clearSessionStateKeys();
          setUser(null);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        if (!isMounted) return;
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    checkAuth();
    
    return () => {
      isMounted = false;
    };
  }, []); // NO dependency on impersonationData - auth should never re-check when impersonation changes

  // Listen for auth changes (simplified - no impersonation logic)
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('🔄 Auth state changed:', event, session?.user?.email);
        
        if (session?.user) {
          const role = await resolveProfileRole({
            supabase,
            userId: session.user.id,
            fallbackRole: getSupabaseUserMetadataRole(session.user),
            onProfileError: (profileError) => {
              console.warn('Profile query error (using metadata role):', {
                message: profileError.message,
                code: profileError.code,
                details: profileError.details,
                hint: profileError.hint
              });
            },
            onFetchError: (profileError) => {
              console.warn('Could not fetch profile role, using metadata:', {
                message: profileError?.message || 'Unknown error',
                name: profileError?.name
              });
            },
            onRoleFound: (profileRole) => {
              console.log('Role from profiles table:', profileRole);
            },
          });
          
          // CRITICAL: user object ALWAYS reflects actual authenticated user
          // NEVER modify based on impersonation - impersonation is separate UI state
          setUser(buildAuthUserFromSupabaseUser(session.user, role));
          
          console.log('👤 User object updated (actual authenticated user):', role);
        } else {
          clearSessionStateKeys();
          setUser(null);
        }
        // Always set loading to false, even if profile fetch fails
        setLoading(false);
        console.log('✅ Loading set to false');
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const login = async (email: string, password: string) => {
    setError(null);
    setLoading(true);
    
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        throw error;
      }

      console.log('✅ Login successful:', data.user);
      
      // Immediately set user state after successful login
      if (data.user) {
        const role = await resolveProfileRole({
          supabase,
          userId: data.user.id,
          fallbackRole: getSupabaseUserMetadataRole(data.user),
          fetchMode: 'single',
          onFetchError: (profileError) => {
            console.log('Could not fetch profile role, using metadata:', profileError);
          },
          onRoleFound: (profileRole) => {
            console.log('Role from profiles table (login):', profileRole);
          },
        });

        setUser(buildAuthUserFromSupabaseUser(data.user, role));

        console.log('👤 User state set immediately after login');
      }
    } catch (error: any) {
      console.error('Login failed:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const register = async (email: string, password: string, first_name?: string, last_name?: string) => {
    setError(null);
    setLoading(true);
    
    try {
      // Generate a stable, unique-ish username to avoid DB uniqueness collisions
      const baseUsername =
        `${(first_name || '')}${(last_name || '')}`.trim() ||
        (email ? email.split('@')[0] : 'user');
      const safeBase = baseUsername.toLowerCase().replace(/[^a-z0-9]/g, '');
      const username = `${safeBase || 'user'}-${Math.random().toString(36).slice(2, 6)}`;

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name,
            last_name,
            username,
            role: 'user'
          }
        }
      });

      if (error) {
        throw error;
      }

      console.log('✅ Registration successful:', data.user);
      
      // Create profile in database with better error handling
      if (data.user) {
        const { error: profileError } = await supabase
          .from('profiles')
          .insert({
            id: data.user.id,
            username
          });

        if (profileError) {
          console.error('Profile creation failed:', profileError);
          // Don't throw error - user is still registered
          // Profile can be created later
        } else {
          console.log('✅ Profile created successfully');
        }
      }
    } catch (error: any) {
      console.error('Registration failed:', error);

      // Friendlier message for duplicate/constraint issues
      const msg = (error?.message || '').toString().toLowerCase();
      if (msg.includes('duplicate key') || msg.includes('already registered') || msg.includes('database error saving new user')) {
        setError('This email is already registered. Try logging in or use a different email.');
      } else {
        setError(error.message || 'Unable to create account. Please try again.');
      }
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      // Clear impersonation data on logout
      localStorage.removeItem('admin_impersonation');
      localStorage.removeItem('admin_session_backup');
      clearSessionStateKeys();
      setImpersonationData(null);
      await supabase.auth.signOut();
      setUser(null);
      console.log('✅ Logout successful');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Store admin session before switching to client account
  const storeAdminSession = async () => {
    try {
      // First refresh the session to ensure we have the freshest tokens
      const { data: refreshedData } = await supabase.auth.refreshSession();
      const session = refreshedData?.session;
      
      if (!session) {
        // Fallback to current session
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        if (!currentSession) {
          throw new Error('No active session to store');
        }
        
        const sessionData = {
          access_token: currentSession.access_token,
          refresh_token: currentSession.refresh_token,
          expires_at: currentSession.expires_at,
          user: {
            id: currentSession.user.id,
            email: currentSession.user.email,
            role: user?.role,
          },
          stored_at: new Date().toISOString(),
        };
        localStorage.setItem('admin_session_backup', JSON.stringify(sessionData));
        console.log('✅ Admin session stored (from current session)');
        return;
      }
      
        const sessionData = {
          access_token: session.access_token,
          refresh_token: session.refresh_token,
          expires_at: session.expires_at,
          user: {
            id: session.user.id,
            email: session.user.email,
            role: user?.role,
          },
          stored_at: new Date().toISOString(),
        };
        localStorage.setItem('admin_session_backup', JSON.stringify(sessionData));
      console.log('✅ Admin session stored (refreshed tokens)');
    } catch (error) {
      console.error('Failed to store admin session:', error);
      throw error;
    }
  };

  // Restore admin session from localStorage
  const restoreAdminSession = async (): Promise<boolean> => {
    try {
      const storedSession = localStorage.getItem('admin_session_backup');
      if (!storedSession) {
        console.warn('No stored admin session found');
        return false;
      }

      const sessionData = JSON.parse(storedSession);
      
      // Check how old the stored session is (refresh tokens typically valid for 7+ days)
      const storedAt = new Date(sessionData.stored_at).getTime();
      const now = Date.now();
      const maxAge = 6 * 24 * 60 * 60 * 1000; // 6 days max (refresh token usually valid for 7 days)
      
      if (now - storedAt > maxAge) {
        console.warn('Stored admin session is too old (> 6 days)');
        localStorage.removeItem('admin_session_backup');
        return false;
      }

      // Try to restore the session using refresh token
      // Supabase will automatically refresh the access token if needed
      console.log('🔄 Attempting to restore admin session...');
      
      const { data, error } = await supabase.auth.setSession({
        access_token: sessionData.access_token,
        refresh_token: sessionData.refresh_token,
      });

      if (error) {
        console.error('Failed to restore admin session with stored tokens:', error);
        
        // If the access token is expired, try refreshing with just the refresh token
        // by signing in with a refresh token grant
        if (error.message?.includes('expired') || error.message?.includes('invalid')) {
          console.log('🔄 Access token expired, trying refresh token...');
          
          // Use refreshSession to get a new access token from the refresh token
          const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession({
            refresh_token: sessionData.refresh_token,
          });
          
          if (refreshError) {
            console.error('Failed to refresh admin session:', refreshError);
            localStorage.removeItem('admin_session_backup');
            return false;
          }
          
          if (refreshData.session) {
            console.log('✅ Admin session restored via refresh token');
            localStorage.removeItem('admin_session_backup');
            localStorage.removeItem('admin_impersonation');
            return true;
          }
        }
        
        localStorage.removeItem('admin_session_backup');
        return false;
      }

      if (data.session) {
        console.log('✅ Admin session restored');
        // Clear the backup and impersonation data after successful restoration
        localStorage.removeItem('admin_session_backup');
        localStorage.removeItem('admin_impersonation');
        return true;
      }

      return false;
    } catch (error) {
      console.error('Failed to restore admin session:', error);
      localStorage.removeItem('admin_session_backup');
      return false;
    }
  };

  // Switch to client user session
  const switchToClientSession = async (accessToken: string, refreshToken: string) => {
    try {
      const { data, error } = await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      });

      if (error) {
        console.error('Failed to switch to client session:', error);
        throw error;
      }

      if (data.session) {
        console.log('✅ Switched to client session');
        // The auth state change listener will update the user state
      } else {
        throw new Error('No session returned');
      }
    } catch (error) {
      console.error('Failed to switch to client session:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    login,
    register,
    logout,
    loading,
    error,
    impersonationData,
    storeAdminSession,
    restoreAdminSession,
    switchToClientSession,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
